/** The battle screen's Presenter: the same shape as the world renderer's.
 *
 * It reads `BattleState` and `GameEvent[]` and decides nothing (I10). The
 * reachable set, the legal attack positions and the active stack all arrive in
 * `BattleView.ui`, computed by the app from the kernel's own `computeReach` and
 * `attackPositions`; nothing here recomputes a rule.
 *
 * The arena is authored at 1280x720, the shape of both shipped viewports, and
 * the stage is scaled to fill the window rather than to the largest whole
 * number that fits: whole-number scaling floored 1920/1280 to 1 and left the
 * battlefield sitting in a 320px black surround. The device resolution stays a
 * whole number and every position below the stage is rounded, so the art still
 * lands on device-pixel boundaries wherever the scale is whole.
 */

import { Application, Container } from "pixi.js";
import type { BattleState, ContentRegistry, GameEvent } from "@game/core";
import { asSpellDefId } from "@game/core";
import type { AtlasLike } from "./deps.js";
import { nearestFiltered } from "./deps.js";
import { Arena, type ArenaOptions } from "./arena.js";
import { StackLayer, type SideColors, DEFAULT_SIDE_COLORS } from "./stacks.js";
import { FxLayer } from "./fx.js";
import { BattleUi, EMPTY_UI, type UiState } from "./ui.js";
import { BattleAnimator, type AnimTarget } from "./animate.js";
import type { HexCoord } from "@game/core";
import type { PixelPoint } from "../coords.js";
import {
  ARENA_H, ARENA_W, arenaFit, asHex, hexGroundLine, pixelToHex, px,
} from "./grid.js";
import type { BattleAction } from "./ui.js";
import { spellKey } from "./keys.js";
import { deviceCapFloor, measureDeviceScale } from "../text/devicePixels.js";
import { setAmbientCapFloor } from "../text/pixi.js";

/** Everything the battle screen is given for a frame. */
export interface BattleView {
  readonly state: BattleState;
  /**
   * Events since the last frame it drew - **for reference only. `update` does
   * not play them.**
   *
   * Animations are played by `animate(events)`, and the order matters: the app
   * must call `animate` with the state the screen is still *showing*, before
   * the re-render that parks every stack on its new hex reaches `update`. The
   * one call site today (`App.tsx`'s battle fixture) does exactly that, through
   * `presenterRef`, and passes `events: []` here. A future screen that fills
   * this field in and expects the board to move will get a still frame.
   */
  readonly events: readonly GameEvent[];
  /** What it re-renders on. */
  readonly version: number;
  readonly ui?: UiState;
  readonly colors?: SideColors;
}

export interface BattlePresenterOptions extends ArenaOptions {
  readonly animations?: boolean;
  /** Background outside the letterboxed arena. */
  readonly letterbox?: number;
  /** Landscape phone: crop the painted console and fill with the hex field. */
  readonly compactChrome?: boolean;
  /**
   * The GPU took the context away, or gave it back.
   *
   * A lost context is the one battle failure that reports itself nowhere. The
   * mount succeeded, so the "renderer failed to mount" path never runs; Pixi
   * raises nothing and keeps ticking against a dead context; the canvas simply
   * stops painting. Everything outside the canvas - the console, the initiative
   * strip, the auto-battle button - is DOM and carries on working, so the game
   * looks alive and the battlefield looks black.
   *
   * It is a phone failure specifically: a battle uploads ~880 MB of texture,
   * roughly double the map's, and a driver that grants that and then wants the
   * memory back takes the context rather than failing the allocation.
   */
  readonly onContextLost?: (lost: boolean) => void;
}

export interface PointerHandlers {
  readonly onHover?: (hex: HexCoord | null, point: PixelPoint) => void;
  readonly onSelect?: (hex: HexCoord | null, point: PixelPoint, button: number) => void;
  /** A console button was pressed. The app decides what, if anything, it means. */
  readonly onCommand?: (action: BattleAction) => void;
  /** Right-click and Escape skip the current animation; the classic players expect it. */
  readonly onSkip?: () => void;
}

/**
 * The pack groups the battle screen draws from. Measured with the `touchedSheets`
 * probe over the battle scenes (`qa/`); a group missing here fails loudly in
 * `Atlas.texture` rather than drawing nothing.
 */
const BATTLE_SHEET_GROUPS: readonly string[] = ["battle", "icons", "objects", "portraits", "units"];

export class BattlePresenter {
  private readonly atlas: AtlasLike;
  private readonly content: ContentRegistry;
  private readonly opts: BattlePresenterOptions;

  private app: Application | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private readonly root = new Container();
  private readonly actors = new Container();
  private readonly arena: Arena;
  private readonly stacks: StackLayer;
  private readonly fx: FxLayer;
  private readonly ui: BattleUi;
  private readonly animator: BattleAnimator;

  private state: BattleState | null = null;
  private version = -1;
  private builtFor = "";
  private lastUi: UiState = EMPTY_UI;
  private compactChrome = false;
  private lastCss = { w: 0, h: 0 };
  private detachPointer: (() => void) | null = null;
  /** Removes the WebGL context-loss listeners; see `onContextLost`. */
  private detachContext: (() => void) | null = null;
  /** Pointer bindings asked for before the canvas existed; see `attachPointer`. */
  private readonly pendingPointer: ((canvas: HTMLCanvasElement) => void)[] = [];

  constructor(atlas: AtlasLike, content: ContentRegistry, opts?: BattlePresenterOptions) {
    this.atlas = nearestFiltered(atlas);
    this.content = content;
    this.opts = opts ?? {};
    this.compactChrome = this.opts.compactChrome === true;
    this.arena = new Arena(this.atlas, this.opts);
    this.stacks = new StackLayer(this.atlas, content);
    this.fx = new FxLayer(this.atlas);
    this.ui = new BattleUi(this.atlas, content);
    this.animator = new BattleAnimator(this.makeTarget(), {
      enabled: this.opts.animations !== false,
    });

    this.actors.label = "battle.actors";
    this.actors.sortableChildren = false; // sorted explicitly, once per frame
    this.root.label = "battle.root";
    this.root.addChild(
      this.arena.background,
      // The lattice, the reach region and the target rings go BETWEEN the two
      // arena ground layers rather than on top of both. Everything lying on the
      // field - the moat's water, the drawbridge deck, the ground dress - is in
      // `arena.groundProps`, and a grid line ruled across any of it is the grid
      // drawn over the world instead of onto it. See `Arena.groundProps`.
      this.ui.under,
      this.arena.groundProps,
      this.arena.shadows,
      // Every stack's contact shadow. This container was built and filled and
      // never added, so no creature on the field had one and the whole army
      // floated a few pixels above the sand.
      this.stacks.shadows,
      this.actors,
      this.stacks.badges,
      // The leather frame is chrome: over the army, so a commander who stands
      // partly under it is cropped by it rather than drawn across it. See
      // `Arena.chrome`.
      this.arena.chrome,
      this.fx.root,
      this.ui.over,
    );
  }

  // ------------------------------------------------------------- lifecycle

  async mount(canvas: HTMLCanvasElement): Promise<void> {
    const app = new Application();
    // The MEASURED device pixels per CSS pixel of this canvas, zoom included -
    // see `measureDeviceScale`. It used to be `floor(devicePixelRatio)`, held
    // at 1 or more: at 80% browser zoom that rendered a 2400-wide backing store
    // for a 1920-column window and let the compositor shrink it, which turned
    // every count plaque into a resample. Fractional above 1 (a 1.5 laptop) is
    // honoured too, and the labels pin themselves to whole device pixels
    // through `snapMagnification` with the resolution folded into their
    // ambient scale - see `resize`.
    const dpr = measureDeviceScale(canvas);
    await app.init({
      canvas,
      antialias: false,
      resolution: dpr,
      autoDensity: true,
      backgroundColor: this.opts.letterbox ?? 0x0a0806,
      width: canvas.clientWidth || ARENA_W,
      height: canvas.clientHeight || ARENA_H,
      preference: "webgl",
    });
    this.app = app;
    this.canvas = canvas;
    // Before the uploads, so a context lost DURING `prepare` is still heard.
    // `preventDefault` on the loss is what makes a restore possible at all -
    // without it the browser never fires `webglcontextrestored`.
    this.detachContext = ((): (() => void) => {
      const lost = (e: Event): void => { e.preventDefault(); this.opts.onContextLost?.(true); };
      const back = (): void => { this.opts.onContextLost?.(false); };
      canvas.addEventListener("webglcontextlost", lost);
      canvas.addEventListener("webglcontextrestored", back);
      return () => {
        canvas.removeEventListener("webglcontextlost", lost);
        canvas.removeEventListener("webglcontextrestored", back);
      };
    })();
    // The battle's sheet groups into this context up front; the CPU copies of
    // the ones nothing reads on the CPU are released - see `Atlas.prepare`.
    await this.atlas.prepare?.(app.renderer, BATTLE_SHEET_GROUPS);
    for (const bind of this.pendingPointer.splice(0)) bind(canvas);
    app.stage.addChild(this.root);
    this.resize(canvas.clientWidth || ARENA_W, canvas.clientHeight || ARENA_H);

    app.ticker.add((ticker) => {
      const dt = ticker.deltaMS;
      this.animator.tick(dt);
      // The idle breath. It runs on the same clock as the animator but is not
      // part of a plan: a plan is finite and resolves, and a board that only
      // moves while a plan is playing is a diorama between turns. It is gated
      // on the same switch as everything else the animator does, so
      // `disableAnimations()` still yields a frame that is the same twice.
      if (this.animator.enabled) this.stacks.tickIdle(dt);
      this.fx.update(dt);
      this.actors.sortChildren();
    });
  }

  /**
   * Landscape phone: the HTML touchbar owns the commands, so crop the painted
   * console and let the hex field fill the leftover height.
   */
  setCompactChrome(on: boolean): void {
    if (this.compactChrome === on) return;
    this.compactChrome = on;
    if (this.lastCss.w > 0) this.resize(this.lastCss.w, this.lastCss.h);
  }

  /**
   * Fill the viewport with the arena. Both target viewports are 16:9, the shape
   * the backdrops are painted at, so this is an exact fill with nothing cropped
   * and no letterbox; `arenaViewRect` is what the chrome is laid out against so
   * that a window of another shape still puts the console on its own bottom edge.
   */
  resize(cssWidth: number, cssHeight: number): void {
    this.lastCss = { w: cssWidth, h: cssHeight };
    const placed = arenaFit(cssWidth, cssHeight, { compactChrome: this.compactChrome });
    const scale = placed.scale;
    // Re-measured on every resize, because a browser zoom change arrives as a
    // resize and moves the device ratio with it; the renderer's resolution
    // follows so the backing store stays one device pixel per device pixel.
    const resolution = this.canvas === null ? 1 : measureDeviceScale(this.canvas);
    this.root.scale.set(scale);
    this.root.position.set(placed.x, placed.y);
    // Every bitmap label under this container has to know what the container is
    // doing to it: at 1.5 a scale-2 glyph texel is worth exactly 3 device pixels
    // and at 1.0672 it would be worth 2.134, which is the difference between a
    // numeral 21 rows tall with 3px strokes and one 15 rows tall with a doubled
    // scanline inside it. `snapMagnification` pins it; this is where the arena's
    // scale reaches the labels. The renderer's resolution is folded in, so the
    // magnification a label lands on is whole in DEVICE pixels, not CSS ones:
    // at 1.5 CSS-per-arena and 0.8 device-per-CSS a scale-2 texel wants 2.4 and
    // gets 2, drawn by the arena at 2 / 2.4 of its nominal size.
    const deviceScale = scale * resolution;
    // The cap floor is the window's, in device rows (15 on a 1080-tall
    // window, 11 on a laptop's), and it is set BEFORE the labels are
    // re-pinned so that every label built or re-pinned from here on holds
    // to it - see `setAmbientCapFloor`.
    setAmbientCapFloor(deviceCapFloor(cssHeight * resolution));
    this.ui.setDeviceScale(deviceScale);
    this.stacks.setDeviceScale(deviceScale);
    this.fx.setDeviceScale(deviceScale);
    this.ui.setViewRect(placed.view);
    this.ui.setConsoleHidden(this.compactChrome);
    this.app?.renderer.resize(cssWidth, cssHeight, resolution);
  }

  update(view: BattleView): void {
    const state = view.state;
    // The budget in docs/specs/presentation.md: no redraw when nothing changed.
    // `ui` is a value object built per frame by the app, so reference equality
    // is the right test - a new object means the pointer or the selection moved.
    if (view.version === this.version && state === this.state
        && (view.ui ?? EMPTY_UI) === this.lastUi && view.colors === undefined) {
      return;
    }
    const signature = `${state.stacks.n}:${state.terrain}:${state.obstacles.length}:${state.siege === null ? 0 : 1}`;
    if (view.colors !== undefined) {
      this.stacks.setSideColors(view.colors);
      this.ui.setSideColors(view.colors);
    } else if (this.state === null) {
      this.stacks.setSideColors(DEFAULT_SIDE_COLORS);
    }
    if (signature !== this.builtFor) {
      this.builtFor = signature;
      // The arena and the stack layer destroy what they put here; anything
      // left is a stray and is destroyed rather than kept alive detached.
      for (const c of this.actors.removeChildren()) c.destroy({ children: true, context: true });
      this.arena.build(state, this.actors);
      this.stacks.build(state, this.actors);
    }
    this.state = state;
    this.version = view.version;
    this.lastUi = view.ui ?? EMPTY_UI;
    this.arena.setGateOpen(state.siege?.gateOpen ?? false);
    this.stacks.update(state);
    this.stacks.setActive(this.lastUi.activeStack);
    // The hovered stack wears an outline too (`StackLayer.setHover`), so the
    // pointer's answer to "what is this" is on the creature and not only in
    // the cursor.
    this.stacks.setHover(this.lastUi.hoverStack ?? null);
    this.ui.update(state, this.lastUi);
    this.actors.sortChildren();
  }

  /** Play the animations implied by `events`, resolving when they have finished. */
  animate(events: readonly GameEvent[], signal?: { readonly aborted: boolean }): Promise<void> {
    return this.animator.animate(events, signal);
  }

  /** Jump to the end of whatever is playing. */
  skip(): void {
    this.animator.skip();
  }

  setAnimationsEnabled(enabled: boolean): void {
    this.animator.enabled = enabled;
    if (!enabled) this.animator.skip();
  }

  /** Freeze at the resting frame and render it. Backs `GameDebug.settle`. */
  async settle(): Promise<void> {
    this.animator.skip();
    this.stacks.settle();
    this.fx.settle();
    if (this.state !== null) {
      this.stacks.update(this.state);
      this.stacks.setActive(this.lastUi.activeStack);
      this.stacks.setHover(this.lastUi.hoverStack ?? null);
      this.ui.update(this.state, this.lastUi);
    }
    this.actors.sortChildren();
    this.app?.render();
    await Promise.resolve();
  }

  get busy(): boolean {
    return this.animator.busy;
  }

  /** The `view.version` the scene currently reflects, for the debug read-out. */
  get renderedVersion(): number {
    return this.version;
  }

  /**
   * Every `destroy` in this package passes `context: true`. Pixi's
   * `Graphics.destroy({ children: true })` deliberately keeps the
   * `GraphicsContext` alive (a context can be shared between graphics), and a
   * context that is never destroyed stays in the renderer's context hash with
   * its geometry and batches. None of ours are shared, and measured 2026-09-12
   * the omission leaked ~8 MB per arena rebuild and ~3 MB per UI repaint.
   */
  dispose(): void {
    this.detachPointer?.();
    this.detachPointer = null;
    this.arena.dispose();
    this.stacks.dispose();
    this.fx.dispose();
    this.ui.dispose();
    this.detachContext?.();
    this.detachContext = null;
    this.root.destroy({ children: false });
    this.app?.destroy(false, { children: true, context: true });
    this.app = null;
    this.canvas = null;
  }

  // --------------------------------------------------------------- pointer

  /** Client coordinates to arena space, undoing the letterbox and the scale. */
  arenaAt(clientX: number, clientY: number): PixelPoint {
    const rect = this.canvas?.getBoundingClientRect();
    const scale = this.root.scale.x || 1;
    const ox = rect === undefined ? 0 : rect.left;
    const oy = rect === undefined ? 0 : rect.top;
    return px((clientX - ox - this.root.x) / scale, (clientY - oy - this.root.y) / scale);
  }

  hexAt(clientX: number, clientY: number): HexCoord | null {
    return pixelToHex(this.arenaAt(clientX, clientY));
  }

  /**
   * D8: the canvas owns pointer events below the panel layer. Nothing here calls
   * `stopPropagation`, so a React panel over the canvas still gets its own.
   */
  attachPointer(handlers: PointerHandlers): () => void {
    let detach: (() => void) | null = null;
    let cancelled = false;
    const canvas = this.canvas;
    if (canvas === null) {
      // Asked for before `mount()` resolved. Returning a silent no-op here is
      // how "units will not move in battle" survived seven rounds of review:
      // the caller believed it had a live board and never found out otherwise.
      // The binding is held instead and made the moment the canvas exists, so
      // no caller can lose its pointer by asking a frame too early.
      this.pendingPointer.push((c) => { if (!cancelled) detach = this.bind(c, handlers); });
    } else {
      detach = this.bind(canvas, handlers);
    }
    return () => {
      cancelled = true;
      detach?.();
      detach = null;
    };
  }

  private bind(canvas: HTMLCanvasElement, handlers: PointerHandlers): () => void {
    const move = (e: PointerEvent): void => {
      const p = this.arenaAt(e.clientX, e.clientY);
      const cmd = this.ui.commandAt(p);
      this.ui.setCommandHover(cmd);
      canvas.style.cursor = cmd === null ? "" : "pointer";
      handlers.onHover?.(cmd === null && !this.ui.overConsole(p) ? pixelToHex(p) : null, p);
    };
    const down = (e: PointerEvent): void => {
      const p = this.arenaAt(e.clientX, e.clientY);
      if (e.button === 2) {
        handlers.onSkip?.();
        this.animator.skip();
      }
      const cmd = this.ui.commandAt(p);
      if (cmd !== null) {
        if (e.button === 0) handlers.onCommand?.(cmd);
        return;
      }
      if (this.ui.overConsole(p)) return;
      handlers.onSelect?.(pixelToHex(p), p, e.button);
    };
    const leave = (): void => {
      this.ui.setCommandHover(null);
      handlers.onHover?.(null, px(-1, -1));
    };
    const menu = (e: Event): void => e.preventDefault();
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerdown", down);
    canvas.addEventListener("pointerleave", leave);
    canvas.addEventListener("contextmenu", menu);
    const detach = (): void => {
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointerleave", leave);
      canvas.removeEventListener("contextmenu", menu);
    };
    this.detachPointer = detach;
    return detach;
  }

  // ---------------------------------------------------------------- target

  private makeTarget(): AnimTarget {
    const stacks = (): StackLayer => this.stacks;
    return {
      hexOf: (s) => stacks().hexOf(s),
      setHex: (s, h) => stacks().setHex(s, h),
      setWalkPoint: (s, a, b, t) => stacks().setWalkPoint(s, a, b, t),
      setPose: (s, p) => stacks().setPose(s, p),
      setView: (s, v) => stacks().setView(s, v),
      setFacing: (s, f) => stacks().setFacing(s, f),
      setFlash: (s, a) => stacks().setFlash(s, a),
      setAlpha: (s, a) => stacks().setAlpha(s, a),
      setNudge: (s, dx, dy) => stacks().setNudge(s, dx, dy),
      setCollapse: (s, t) => stacks().setCollapse(s, t),
      removeStack: (s) => stacks().removeStack(s),
      floater: (s, text, kind) => {
        const a = stacks().anchorOfStack(s);
        if (a !== null) this.fx.floater(a.x, a.y, text, kind);
      },
      banner: (s, kind, good) => {
        const a = stacks().anchorOfStack(s);
        if (a !== null) this.fx.banner(a.x, a.y - 12, kind, good);
      },
      projectile: (fromHex, toHex, t) => {
        if (t < 0) { this.fx.setProjectile(0, 0, 0, 0, -1); return; }
        // Chest to chest, off the stacks that are actually standing there, not
        // off the two hexes: `fx.ts` cannot know how tall a creature is drawn,
        // and a fixed lift was correct only while every creature was the same
        // 65px tall. `stackAt` falls back to the hex when the shooter or the
        // target is not a live view (a wall segment, say).
        const a = this.stacks.chestOfHex(fromHex);
        const b = this.stacks.chestOfHex(toHex);
        this.fx.setProjectile(a.x, a.y, b.x, b.y, t);
      },
      spellFx: (hex, spell, t) => {
        if (t < 0) { this.fx.setSpell(0, 0, null, -1); return; }
        // `BattleSpellCast.target` is a hex; a slot-shaped value would be out of
        // range for the board, so anything off-board falls back to the centre.
        const h = hex >= 0 && hex < 165 ? hex : 82;
        const p = hexGroundLine(asHex(h));
        this.fx.setSpell(p.x, p.y, this.spellIcon(spell), t);
      },
      setWall: (segment, integrity) => this.arena.setWallIntegrity(segment, integrity),
      settle: () => {
        stacks().settle();
        this.fx.settle();
      },
    };
  }

  private spellIcon(spellId: string): string | null {
    const def = this.content.spells.get(asSpellDefId(spellId));
    return def === undefined ? null : spellKey(def.art);
  }
}
